import userDetail from "Stores/userDetail";
import onboardingCopilotStore from "Stores/onboardingCopilotStore";

/**
 * Decides whether the copilot should open in onboarding (new user) or digest
 * (existing user, first copilot visit) mode. New user = a workspace with no
 * data yet, the same signal DashboardRouting uses to land them on /copilot.
 * The digest fires once per member, gated by its own localStorage key.
 */

// Dev/QA override: set localStorage sg_copilot_onboarding_force to "onboarding"
// or "digest" (or "1" for onboarding) to run that flow on ANY account,
// bypassing the new-vs-existing check and the once-only gates so it can be
// re-run repeatedly. Harmless in production unless the key is set by hand.
const forcedMode = () => {
  try {
    const v = localStorage.getItem("sg_copilot_onboarding_force");
    if (!v) return null;
    return v === "digest" ? "digest" : "onboarding";
  } catch (e) {
    return null;
  }
};

const digestKey = memberId => `sg_copilot_digest_${memberId || "anon"}`;

const digestShown = memberId => {
  try {
    return localStorage.getItem(digestKey(memberId)) === "1";
  } catch (e) {
    return true; // storage unavailable: never nag
  }
};

const markDigestShown = memberId => {
  try {
    localStorage.setItem(digestKey(memberId), "1");
  } catch (e) {
    /* best-effort */
  }
};

export const isNewWorkspace = () => {
  const ff = userDetail?.userFeatureFlag || {};
  return !((ff.noOfContactsUsed || 0) > 0 || (ff.noOfContactsAddedInTeam || 0) > 0);
};

// New-user onboarding only, no side effects. Used for the dashboard auto-open.
export const shouldAutoOpenOnboarding = () => {
  if (forcedMode() === "onboarding") return true;
  const memberId = userDetail?.userInfo?.memberId;
  if (!memberId) return false;
  return isNewWorkspace() && !!onboardingCopilotStore.shouldOffer(memberId);
};

// Full picker for an actual copilot visit: onboarding for new users, a one-time
// digest for existing users. Marks the digest as shown when it returns "digest".
export const pickCopilotOnboardingMode = () => {
  const forced = forcedMode();
  if (forced) return forced;
  const memberId = userDetail?.userInfo?.memberId;
  if (!memberId) return null;
  if (isNewWorkspace()) {
    return onboardingCopilotStore.shouldOffer(memberId) ? "onboarding" : null;
  }
  if (!digestShown(memberId)) {
    markDigestShown(memberId);
    return "digest";
  }
  return null;
};

export default { isNewWorkspace, shouldAutoOpenOnboarding, pickCopilotOnboardingMode };
